import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, Building2, Plus, Edit3, Trash2, Save, X, Loader2,
  Home as HomeIcon, TrendingUp, Calculator, MapPin, Map as MapIcon, List as ListIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default Leaflet icon paths for bundlers
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const goldIcon = L.divIcon({
  className: 'lh-marker',
  html: '<div style="width:24px;height:24px;border-radius:50%;background:#C4A55A;border:3px solid #1a1a1a;box-shadow:0 0 12px rgba(196,165,90,0.7);"></div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const fmtCHF = (n) => new Intl.NumberFormat('de-CH', { maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtPct = (n) => `${(Number(n) || 0).toFixed(2)} %`;

const PROPERTY_TYPES = [
  { value: 'residential', label: 'Wohnen' },
  { value: 'commercial', label: 'Gewerbe' },
  { value: 'mixed', label: 'Gemischt' },
  { value: 'land', label: 'Grundstück' },
];

const emptyForm = () => ({
  address: '', propertyType: 'residential',
  marketValue: 0, mortgage: 0, annualNOI: 0, capRate: '',
  yearAcquired: '', notes: '',
  latitude: null, longitude: null,
  city: '', postalCode: '', country: '', countryCode: '',
});

export const AdminProperties = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [form, setForm] = useState(emptyForm());
  const [view, setView] = useState('list'); // 'list' | 'map'

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/admin/login'); return; }
    load();
  }, [user, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(apiBase() + '/api/admin/valuation/properties', { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Fehler beim Laden');
      setList(d.properties || []);
      setTotals(d.totals);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => { setForm(emptyForm()); setEditing('new'); };
  const startEdit = (p) => {
    setForm({
      address: p.address || '',
      propertyType: p.propertyType || 'residential',
      marketValue: p.marketValue || 0,
      mortgage: p.mortgage || 0,
      annualNOI: p.annualNOI || 0,
      capRate: p.capRate || '',
      yearAcquired: p.yearAcquired || '',
      notes: p.notes || '',
      latitude: p.latitude || null,
      longitude: p.longitude || null,
      city: p.city || '',
      postalCode: p.postalCode || '',
      country: p.country || '',
      countryCode: p.countryCode || '',
    });
    setEditing(p.id);
  };

  const save = async () => {
    if (!form.address.trim()) { toast.error('Adresse erforderlich'); return; }
    setBusy(true);
    try {
      const body = {
        address: form.address.trim(),
        propertyType: form.propertyType,
        marketValue: parseFloat(form.marketValue) || 0,
        mortgage: parseFloat(form.mortgage) || 0,
        annualNOI: parseFloat(form.annualNOI) || 0,
        capRate: form.capRate ? parseFloat(form.capRate) : null,
        yearAcquired: form.yearAcquired ? parseInt(form.yearAcquired, 10) : null,
        notes: form.notes,
        latitude: form.latitude,
        longitude: form.longitude,
        city: form.city,
        postalCode: form.postalCode,
        country: form.country,
        countryCode: form.countryCode,
      };
      const isNew = editing === 'new';
      const url = isNew ? '/api/admin/valuation/properties' : `/api/admin/valuation/properties/${editing}`;
      const r = await fetch(apiBase() + url, {
        method: isNew ? 'POST' : 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Speichern fehlgeschlagen');
      toast.success(isNew ? 'Immobilie hinzugefügt' : 'Immobilie aktualisiert');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Diese Immobilie wirklich löschen?')) return;
    setBusy(true);
    try {
      const r = await fetch(apiBase() + `/api/admin/valuation/properties/${id}`, {
        method: 'DELETE', credentials: 'include',
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Löschen fehlgeschlagen');
      toast.success('Gelöscht');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const liveCapRate = (() => {
    const mv = parseFloat(form.marketValue) || 0;
    const noi = parseFloat(form.annualNOI) || 0;
    return mv > 0 && noi > 0 ? ((noi / mv) * 100).toFixed(2) : '—';
  })();

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-[#C4A55A]/20 bg-black/80 backdrop-blur-md sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard" className="text-gray-400 hover:text-[#C4A55A]" data-testid="back-to-admin">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <Building2 className="h-6 w-6 text-[#C4A55A]" />
            <h1 className="text-xl font-semibold">Immobilien-Portfolio</h1>
            <span className="text-xs text-gray-500 ml-2">{list.length} Objekte</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-[#1a1a1a] border border-white/15 rounded-lg p-0.5" data-testid="view-toggle">
              <button
                onClick={() => setView('list')}
                data-testid="view-list"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs ${view === 'list' ? 'bg-[#C4A55A] text-black' : 'text-gray-400'}`}
              ><ListIcon className="h-3.5 w-3.5" /> Liste</button>
              <button
                onClick={() => setView('map')}
                data-testid="view-map"
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs ${view === 'map' ? 'bg-[#C4A55A] text-black' : 'text-gray-400'}`}
              ><MapIcon className="h-3.5 w-3.5" /> Karte</button>
            </div>
            <Link to="/admin/valuation"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#1a1a1a] border border-white/15 text-sm text-gray-200 hover:bg-white/5">
              <Calculator className="h-4 w-4" /> Zur Bewertung
            </Link>
            <button
              onClick={startNew}
              data-testid="property-new-btn"
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black text-sm font-semibold"
            >
              <Plus className="h-4 w-4" /> Neue Immobilie
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Totals row */}
        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="property-totals">
            <Stat label="Anzahl" value={totals.count} />
            <Stat label="Marktwert Σ" value={`CHF ${fmtCHF(totals.marketValue)}`} accent />
            <Stat label="Hypotheken Σ" value={`CHF ${fmtCHF(totals.mortgage)}`} />
            <Stat label="NAV (Equity)" value={`CHF ${fmtCHF(totals.nav)}`} highlight />
            <Stat label="Portfolio Cap Rate" value={fmtPct(totals.portfolioCapRate)} />
          </div>
        )}

        {/* Edit form */}
        {editing && (
          <section className="bg-[#0f0f0f] border border-[#C4A55A]/40 rounded-2xl p-6 mb-6" data-testid="property-form">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <HomeIcon className="h-5 w-5 text-[#C4A55A]" />
                {editing === 'new' ? 'Neue Immobilie erfassen' : 'Immobilie bearbeiten'}
              </h2>
              <button onClick={() => setEditing(null)} className="text-gray-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <label className="block sm:col-span-2 relative">
                <span className="text-xs text-gray-400 flex items-center gap-1">
                  <MapPin className="h-3 w-3" /> Adresse (mit OpenStreetMap-Autocomplete)
                </span>
                <AddressAutocomplete
                  value={form.address}
                  onChange={(addr) => setForm({ ...form, address: addr })}
                  onSelect={(suggestion) => {
                    setForm({
                      ...form,
                      address: suggestion.displayName,
                      city: suggestion.city,
                      postalCode: suggestion.postalCode,
                      country: suggestion.country,
                      countryCode: suggestion.countryCode,
                      latitude: suggestion.latitude,
                      longitude: suggestion.longitude,
                    });
                  }}
                />
                {form.latitude && (
                  <div className="text-[10px] text-emerald-400 mt-1">
                    📍 Geo-Koordinaten erkannt: {form.latitude?.toFixed(4)}, {form.longitude?.toFixed(4)}
                    {form.postalCode && ` · ${form.postalCode} ${form.city}`}
                  </div>
                )}
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Objekttyp</span>
                <select value={form.propertyType}
                  onChange={(e) => setForm({ ...form, propertyType: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white"
                  data-testid="property-type">
                  {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Kaufjahr (optional)</span>
                <input type="number" min="1900" max={new Date().getFullYear()}
                  value={form.yearAcquired}
                  onChange={(e) => setForm({ ...form, yearAcquired: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white"
                  data-testid="property-year" />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Marktwert (CHF)</span>
                <input type="number" min="0" step="1000"
                  value={form.marketValue}
                  onChange={(e) => setForm({ ...form, marketValue: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-[#C4A55A]/40 rounded-lg px-3 py-2 text-white tabular-nums font-semibold"
                  data-testid="property-marketvalue" />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Hypothek (CHF)</span>
                <input type="number" min="0" step="1000"
                  value={form.mortgage}
                  onChange={(e) => setForm({ ...form, mortgage: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white tabular-nums"
                  data-testid="property-mortgage" />
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Jährlicher NOI nach Bewirtschaftung (CHF)</span>
                <input type="number" min="0" step="100"
                  value={form.annualNOI}
                  onChange={(e) => setForm({ ...form, annualNOI: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white tabular-nums"
                  data-testid="property-noi" />
                <span className="text-[10px] text-gray-500">Mieten minus Bewirtschaftungskosten, vor Zinsen & Steuern</span>
              </label>
              <label className="block">
                <span className="text-xs text-gray-400">Cap Rate (%) – automatisch wenn leer</span>
                <input type="number" step="0.01" min="0"
                  value={form.capRate}
                  onChange={(e) => setForm({ ...form, capRate: e.target.value })}
                  placeholder={liveCapRate}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white tabular-nums"
                  data-testid="property-caprate" />
                <span className="text-[10px] text-gray-500">Berechnet: {liveCapRate} %</span>
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs text-gray-400">Notizen</span>
                <textarea rows={2} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white text-sm" />
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-gray-400 hover:text-white text-sm">Abbrechen</button>
              <button onClick={save} disabled={busy} data-testid="property-save"
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-[#C4A55A] to-[#B8974E] text-black font-semibold disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Speichern
              </button>
            </div>
          </section>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-20 text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Laden…
          </div>
        ) : !list.length ? (
          <div className="text-center py-20 bg-[#0f0f0f] rounded-xl border border-white/5">
            <Building2 className="h-10 w-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 mb-2">Noch keine Immobilien erfasst.</p>
            <button onClick={startNew} className="text-[#C4A55A] underline text-sm">Erste Immobilie hinzufügen →</button>
          </div>
        ) : view === 'map' ? (
          <PortfolioMap properties={list} />
        ) : (
          <div className="bg-[#0f0f0f] border border-white/5 rounded-xl overflow-hidden" data-testid="property-list">
            <table className="w-full text-sm">
              <thead className="bg-black/40 text-xs uppercase tracking-wider text-gray-400">
                <tr>
                  <th className="text-left py-3 px-4">Adresse</th>
                  <th className="text-left py-3 px-4">Typ</th>
                  <th className="text-right py-3 px-4">Marktwert</th>
                  <th className="text-right py-3 px-4">Hypothek</th>
                  <th className="text-right py-3 px-4">Equity</th>
                  <th className="text-right py-3 px-4">NOI</th>
                  <th className="text-right py-3 px-4">Cap Rate</th>
                  <th className="text-right py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((p) => {
                  const equity = (p.marketValue || 0) - (p.mortgage || 0);
                  return (
                    <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`property-row-${p.id}`}>
                      <td className="py-3 px-4">
                        <div className="font-medium">{p.address}</div>
                        {p.yearAcquired && <div className="text-xs text-gray-500">Kauf {p.yearAcquired}</div>}
                      </td>
                      <td className="py-3 px-4 text-gray-400">
                        {PROPERTY_TYPES.find((t) => t.value === p.propertyType)?.label || p.propertyType}
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums">CHF {fmtCHF(p.marketValue)}</td>
                      <td className="py-3 px-4 text-right tabular-nums text-gray-400">CHF {fmtCHF(p.mortgage)}</td>
                      <td className="py-3 px-4 text-right tabular-nums text-[#C4A55A] font-semibold">CHF {fmtCHF(equity)}</td>
                      <td className="py-3 px-4 text-right tabular-nums">CHF {fmtCHF(p.annualNOI)}</td>
                      <td className="py-3 px-4 text-right tabular-nums">{fmtPct(p.capRate)}</td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => startEdit(p)} className="text-gray-400 hover:text-[#C4A55A] mr-2"
                                data-testid={`property-edit-${p.id}`}>
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button onClick={() => remove(p.id)} className="text-gray-400 hover:text-red-400"
                                data-testid={`property-delete-${p.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totals && totals.count > 0 && (
          <div className="mt-6 bg-gradient-to-br from-[#C4A55A]/10 to-transparent border border-[#C4A55A]/30 rounded-xl p-5" data-testid="nav-summary">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-[#C4A55A]" />
              <h3 className="text-sm uppercase tracking-wider text-[#C4A55A]">NAV-Berechnung</h3>
            </div>
            <p className="text-sm text-gray-300">
              Bei einem Portfolio-Marktwert von <strong className="text-white">CHF {fmtCHF(totals.marketValue)}</strong>{' '}
              und Hypotheken von <strong>CHF {fmtCHF(totals.mortgage)}</strong> beträgt der{' '}
              <strong className="text-[#C4A55A]">Net Asset Value (NAV) CHF {fmtCHF(totals.nav)}</strong>.{' '}
              Bei jährlichen Netto-Mieteinnahmen von <strong>CHF {fmtCHF(totals.annualNOI)}</strong>{' '}
              ergibt sich eine durchschnittliche Cap Rate von <strong>{fmtPct(totals.portfolioCapRate)}</strong>.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

const Stat = ({ label, value, accent, highlight }) => (
  <div className={`rounded-xl p-4 ${
    highlight ? 'bg-[#C4A55A]/15 border border-[#C4A55A]/40' :
    accent ? 'bg-black/40 border border-[#C4A55A]/20' :
    'bg-black/40 border border-white/5'
  }`}>
    <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</div>
    <div className={`text-lg font-bold tabular-nums ${highlight ? 'text-[#C4A55A]' : 'text-white'}`}>{value}</div>
  </div>
);

const apiBaseHere = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const AddressAutocomplete = ({ value, onChange, onSelect }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const handleChange = (val) => {
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val || val.length < 3) {
      setSuggestions([]); setOpen(false); return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await fetch(apiBaseHere() + `/api/admin/geo/search?q=${encodeURIComponent(val)}`, {
          credentials: 'include',
        });
        const d = await r.json();
        if (r.ok && d.success) {
          setSuggestions(d.results || []);
          setOpen(true);
        }
      } catch { /* ignore */ }
      finally { setLoading(false); }
    }, 350);
  };

  const pickSuggestion = (s) => {
    onSelect(s);
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => suggestions.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        className="mt-1 w-full bg-black/60 border border-white/15 rounded-lg px-3 py-2 text-white"
        data-testid="property-address"
        placeholder="z.B. Bahnhofstrasse 1, 8001 Zürich – tippen für Vorschläge…"
        autoComplete="off"
      />
      {loading && <Loader2 className="h-4 w-4 animate-spin absolute right-3 top-3.5 text-gray-400" />}
      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-[#1a1a1a] border border-[#C4A55A]/30 rounded-lg max-h-64 overflow-y-auto shadow-2xl"
             data-testid="address-suggestions">
          {suggestions.map((s, i) => (
            <button
              key={`${s.osmId}-${i}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pickSuggestion(s)}
              data-testid={`address-suggest-${i}`}
              className="w-full text-left px-3 py-2 hover:bg-[#C4A55A]/10 border-b border-white/5 last:border-b-0"
            >
              <div className="text-sm text-white">{s.line1 || s.displayName.split(',')[0]}</div>
              <div className="text-xs text-gray-400 truncate">{s.postalCode} {s.city}, {s.country}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PortfolioMap = ({ properties }) => {
  const geo = (properties || []).filter((p) => p.latitude && p.longitude);
  if (!geo.length) {
    return (
      <div className="bg-[#0f0f0f] border border-white/10 rounded-xl p-10 text-center" data-testid="map-empty">
        <MapIcon className="h-10 w-10 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400">Keine Immobilien mit Geo-Koordinaten erfasst.</p>
        <p className="text-xs text-gray-500 mt-1">Beim Anlegen einer Immobilie einen Vorschlag aus der OpenStreetMap-Suche auswählen.</p>
      </div>
    );
  }
  // Center on Switzerland by default, or first property
  const center = [geo[0].latitude, geo[0].longitude];
  // Compute bounds for zoom-to-fit
  const lats = geo.map((p) => p.latitude);
  const lngs = geo.map((p) => p.longitude);
  const bounds = geo.length > 1
    ? [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]]
    : null;

  return (
    <div className="bg-[#0f0f0f] border border-white/10 rounded-xl overflow-hidden" data-testid="map-container">
      <MapContainer
        center={center}
        zoom={8}
        bounds={bounds}
        boundsOptions={{ padding: [40, 40] }}
        style={{ height: '560px', width: '100%', background: '#0a0a0a' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {geo.map((p) => {
          const equity = (p.marketValue || 0) - (p.mortgage || 0);
          return (
            <Marker key={p.id} position={[p.latitude, p.longitude]} icon={goldIcon}>
              <Popup>
                <div className="text-sm" style={{ minWidth: 220 }}>
                  <div style={{ fontWeight: 'bold', color: '#C4A55A', marginBottom: 4 }}>{p.address}</div>
                  <div>Marktwert: <strong>CHF {fmtCHF(p.marketValue)}</strong></div>
                  <div>Hypothek: CHF {fmtCHF(p.mortgage)}</div>
                  <div>Equity: <strong>CHF {fmtCHF(equity)}</strong></div>
                  <div>NOI: CHF {fmtCHF(p.annualNOI)}</div>
                  <div>Cap Rate: {fmtPct(p.capRate)}</div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      <div className="px-4 py-2 text-xs text-gray-500 bg-black/60">
        {geo.length} Standorte · Karten-Daten © OpenStreetMap-Mitwirkende
      </div>
    </div>
  );
};
